import { useForm } from "react-hook-form";
import Button from "../../../student/components/generic/button";
import React, { useState } from "react";
import { executeQuery, REACT_APP_HOMEPAGE_LINK } from "../../../utils";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useLogin } from "../../../utils/auth/login";
import { LicenseInviteState } from "./types";
import { processPasswordForRequest } from "../../../utils/processPasswordForRequest";
import { InviteGoogleAuth } from "./InviteGoogleAuth";

interface FormInputs extends Record<string, unknown> {
  first: string;
  last: string;
  password: string;
}

// Note: this is a separate component to avoid re-rendering the entire
// InviteCreateAccount component (including GoogleLoginButton) on every
// keystroke
const AccountForm: React.FC<{
  email: string;
  accountType: "parent" | "learner";
  setInviteState: (state: LicenseInviteState) => void;
}> = ({ email, accountType, setInviteState }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>();
  const login = useLogin();

  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [nonFieldError, setNonFieldError] = useState<string | undefined>();

  const { mutate: createAccountMutation } = useMutation({
    mutationFn: (data: FormInputs) =>
      executeQuery({
        path: "/learner/default/create-account",
        method: "post",
        requestBody: {
          ...data,
          email,
          password: processPasswordForRequest(data.password),
          accountType,
        },
      }),
  });

  const onSubmit = (data: FormInputs) => {
    setIsCreatingAccount(true);
    createAccountMutation(data, {
      onSuccess: async () => {
        try {
          const { success } = await login(email, data.password, "learner");
          if (success) {
            setInviteState({ state: "acceptInvite" });
          } else {
            setNonFieldError("There was an error while logging in");
          }
        } catch (e: any) {
          setNonFieldError("There was an error while logging in");
        }
      },
      onError: () => {
        setIsCreatingAccount(false);
        setNonFieldError("There was an error while creating your account");
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <span className="text-sm font-bold">Email</span>
        <span>{email}</span>
      </div>

      <div className="flex flex-col items-start gap-1">
        <label className="text-sm font-bold" htmlFor="first">
          First Name
        </label>
        <input
          className="h-10 w-full rounded px-3 py-0"
          type="text"
          id="first"
          {...register("first", { required: "This field is required" })}
        />
        {errors.first && (
          <span className="text-sm text-dm-error-500">
            {errors.first.message}
          </span>
        )}
      </div>

      <div className="flex flex-col items-start gap-1">
        <label className="text-sm font-bold" htmlFor="last">
          Last Name
        </label>
        <input
          className="h-10 w-full rounded px-3 py-0"
          type="text"
          id="last"
          {...register("last", { required: "This field is required" })}
        />
        {errors.last && (
          <span className="text-sm text-dm-error-500">
            {errors.last.message}
          </span>
        )}
      </div>

      <div className="flex flex-col items-start gap-1">
        <label className="text-sm font-bold" htmlFor="password">
          Password
        </label>
        <input
          className="h-10 w-full rounded px-3 py-0"
          type="password"
          id="password"
          {...register("password", { required: "This field is required" })}
        />
        {errors.password && (
          <span className="text-sm text-dm-error-500">
            {errors.password.message}
          </span>
        )}
      </div>

      {nonFieldError && (
        <div className="rounded border border-dm-error-200 bg-dm-error-100 p-4 text-center text-sm text-dm-error-500">
          {nonFieldError}
        </div>
      )}

      <Button isLoading={isCreatingAccount}>Continue</Button>
    </form>
  );
};

export const InviteCreateAccount: React.FC<{
  email: string;
  accountType: "parent" | "learner";
  setInviteState: (state: LicenseInviteState) => void;
}> = ({ email, accountType, setInviteState }) => {
  return (
    <div className="flex w-full flex-col gap-6">
      <div className="mb-2 flex flex-col gap-2 text-center">
        <h1 className="font-serif text-2xl font-bold text-dm-brand-blue-600">
          Sign Up
        </h1>
        <p className="font-serif text-lg font-bold">
          We just need a few details to create your account.
        </p>
      </div>

      <AccountForm
        email={email}
        accountType={accountType}
        setInviteState={setInviteState}
      />

      <span className="w-full text-center font-bold">Or</span>

      <InviteGoogleAuth
        accountType={accountType}
        setInviteState={setInviteState}
      />

      <p className="text-center text-xs text-dm-charcoal-500">
        By signing up, you accept the
        <br />
        DeltaMath for Home{" "}
        <Link
          className="underline underline-offset-2 hover:text-dm-charcoal-800 hover:no-underline"
          to={`${REACT_APP_HOMEPAGE_LINK}/terms-policies`}
          target="_blank"
        >
          Terms &amp; Policies
        </Link>
      </p>
    </div>
  );
};
